import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  IconLoadingComponent,
  IconStoreComponent,
} from '@pedix-workspace/angular-ui-icons';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { CorreoArgentinoAgency, formatAgencyHours } from '@pedix-workspace/utils';

export { CorreoArgentinoAgency } from '@pedix-workspace/utils';

export type BranchOption = {
  id: string;
  name: string;
  address: string;
  locality: string;
  city: string;
  schedule?: string;
  agency: CorreoArgentinoAgency;
  distanceKm: number | null;
  isClosest: boolean;
};

const MAX_BRANCHES_TO_LOAD = 6;
const INITIAL_BRANCHES_VISIBLE = 3;

type GetAgenciesResponse = {
  success: boolean;
  agencies?: CorreoArgentinoAgency[];
  error?: string;
};

const AGENCIES_ENDPOINT = '/shipping/agencies/';

@Component({
  selector: 'pxw-branch-selector',
  standalone: true,
  templateUrl: './branch-selector.component.html',
  styleUrls: ['./branch-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    FormsModule,
    AlertComponent,
    IconLoadingComponent,
    IconStoreComponent,
  ],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class BranchSelectorComponent {
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  private readonly loadAgencies$ = new Subject<{
    stateId?: string;
    postalCode?: string;
  }>();

  readonly apiUrl = input.required<string>();
  readonly establishmentId = input.required<string>();
  readonly stateId = input<string>();
  readonly postalCode = input<string>();
  readonly isFormSubmitted = input<boolean>(false);
  readonly selectedBranchId = input<string | null>(null);
  readonly userCoordinates = input<{ lat: number; lng: number } | null>(null);

  readonly selectedBranchChange = output<CorreoArgentinoAgency | null>();

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly agencies = signal<CorreoArgentinoAgency[]>([]);
  readonly showAllBranches = signal(false);

  readonly selectedBranchIdInternal = signal<string | null>(null);

  readonly branchOptions = computed<BranchOption[]>(() => {
    const userCoords = this.userCoordinates();
    const activeAgencies = this.agencies().filter(
      agency => agency.status === 'ACTIVE' && agency.services?.pickupAvailability !== false,
    );

    const optionsWithDistance = activeAgencies
      .filter(agency => agency.location?.latitude && agency.location?.longitude)
      .map(agency => {
        const agencyLat = parseFloat(agency.location.latitude);
        const agencyLng = parseFloat(agency.location.longitude);
        const distanceKm =
          userCoords && !isNaN(agencyLat) && !isNaN(agencyLng)
            ? this.calculateDistanceKm(userCoords.lat, userCoords.lng, agencyLat, agencyLng)
            : null;

        return {
          id: agency.code,
          name: agency.name,
          address: `${agency.location.address?.streetName || ''} ${agency.location.address?.streetNumber || ''}`.trim(),
          locality: agency.location.address?.locality || '',
          city: agency.location.address?.city || '',
          schedule: formatAgencyHours(agency.hours),
          agency,
          distanceKm,
          isClosest: false,
        };
      });

    const hasDistances = optionsWithDistance.some(o => o.distanceKm !== null);
    if (hasDistances) {
      optionsWithDistance.sort((a, b) => {
        if (a.distanceKm === null) return 1;
        if (b.distanceKm === null) return -1;
        return a.distanceKm - b.distanceKm;
      });
    }

    const limited = optionsWithDistance.slice(0, MAX_BRANCHES_TO_LOAD);

    if (limited.length > 0 && limited[0].distanceKm !== null) {
      return limited.map((option, index) =>
        index === 0 ? { ...option, isClosest: true } : option
      );
    }

    return limited;
  });

  readonly visibleBranches = computed(() => {
    const all = this.branchOptions();
    if (this.showAllBranches()) {
      return all;
    }
    return all.slice(0, INITIAL_BRANCHES_VISIBLE);
  });

  readonly hasMoreBranches = computed(() => {
    return this.branchOptions().length > INITIAL_BRANCHES_VISIBLE;
  });

  readonly hiddenBranchesCount = computed(() => {
    return Math.max(0, this.branchOptions().length - INITIAL_BRANCHES_VISIBLE);
  });

  toggleShowAll(): void {
    this.showAllBranches.update(v => !v);
  }

  onBranchSelected(branchId: string): void {
    this.selectedBranchIdInternal.set(branchId);
  }

  formatAddress(address: string): string {
    return address
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private calculateDistanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371;
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c * 10) / 10;
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  constructor() {
    this.setupAgenciesLoader();

    effect(
      () => {
        this.selectedBranchIdInternal.set(this.selectedBranchId());
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      const branchId = this.selectedBranchIdInternal();
      if (!branchId) {
        this.selectedBranchChange.emit(null);
        return;
      }

      const selectedOption = this.branchOptions().find(option => option.id === branchId);
      if (!selectedOption) {
        if (!this.loading()) {
          this.selectedBranchIdInternal.set(null);
        }
        return;
      }

      this.selectedBranchChange.emit(selectedOption.agency);
    });

    effect(() => {
      const stateId = this.stateId();
      const postalCode = this.postalCode();

      if (stateId || postalCode) {
        this.loadAgencies$.next({ stateId, postalCode });
      }
    });
  }

  private setupAgenciesLoader(): void {
    this.loadAgencies$
      .pipe(
        distinctUntilChanged(
          (prev, curr) => prev.stateId === curr.stateId && prev.postalCode === curr.postalCode,
        ),
        filter(({ stateId, postalCode }) => !!(stateId || postalCode)),
        tap(() => {
          this.loading.set(true);
          this.error.set(null);
        }),
        switchMap(({ stateId, postalCode }) =>
          this.http
            .post<GetAgenciesResponse>(`${this.apiUrl()}${AGENCIES_ENDPOINT}`, {
              establishmentId: this.establishmentId(),
              stateId,
              postalCode,
            })
            .pipe(
              catchError(err => {
                console.error('Error fetching agencies:', err);
                return of<GetAgenciesResponse>({
                  success: false,
                  error: 'Error de conexión al obtener sucursales',
                });
              }),
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(response => {
        this.loading.set(false);

        if (response.success && response.agencies) {
          this.agencies.set(response.agencies);
        } else {
          this.error.set(response.error || 'Error al obtener sucursales');
          this.agencies.set([]);
        }
      });
  }

}
